import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  ClipboardDocumentCheckIcon,
  LockClosedIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import { Badge } from '../../components/ui';
import { cn } from '../../shared/lib/cn';
import {
  CLIENT_NAME,
  PERIOD_LABEL,
  approvalGates,
  probableMatters,
  unusualItems,
} from './data';
import { formatCurrency } from './utils';

const PAGE_TITLES: Record<string, string> = {
  '/judgmental-accruals': 'EC-02e Workpaper',
  '/judgmental-accruals/source-log': 'Source Log',
  '/judgmental-accruals/instructions': 'Agent Instructions',
  '/judgmental-accruals/config': 'Configuration',
};

export function JudgmentalAccrualsLayout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const title = PAGE_TITLES[pathname] ?? 'Judgmental Accruals';
  const totalAccrual = probableMatters.reduce((sum, matter) => sum + matter.recommendedAccrual, 0);
  const warrantMtm = unusualItems.find(item => item.id === 'UNK-APR-01')?.amount ?? 0;

  return (
    <Layout>
      <div className="flex h-full flex-col bg-gray-50 dark:bg-dark-bg">
        <div className="border-b border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
          <div className="mx-auto max-w-[1680px] px-6 py-4">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="danger" className="gap-1.5">
                    <LockClosedIcon className="h-3.5 w-3.5" />
                    Tier 3
                  </Badge>
                  <Badge variant="outline">{PERIOD_LABEL}</Badge>
                  <Badge variant="secondary">{CLIENT_NAME}</Badge>
                </div>
                <h1 className="mt-3 text-xl font-semibold text-charcoal dark:text-gray-100">
                  {title}
                </h1>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:min-w-[560px]">
                <Kpi label="Probable matters" value={String(probableMatters.length)} icon={ScaleIcon} />
                <Kpi label="Accrual" value={formatCurrency(totalAccrual, { compact: true })} tone="danger" />
                <Kpi label="Warrant MTM" value={formatCurrency(warrantMtm, { compact: true })} tone="success" />
                <Kpi label="Gates" value={String(approvalGates.length)} icon={ClipboardDocumentCheckIcon} />
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </Layout>
  );
}

function Kpi({
  label,
  value,
  tone = 'neutral',
  icon: Icon,
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'danger' | 'success';
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-dark-border dark:bg-dark-surface-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-medium uppercase text-gray-500 dark:text-gray-400">
          {label}
        </span>
        {Icon && <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />}
      </div>
      <div
        className={cn(
          'mt-1 text-lg font-semibold text-charcoal dark:text-gray-100',
          tone === 'danger' && 'text-rose-600 dark:text-rose-400',
          tone === 'success' && 'text-emerald-600 dark:text-emerald-400',
        )}
      >
        {value}
      </div>
    </div>
  );
}

export default JudgmentalAccrualsLayout;
